import { FC, useState } from 'react'
import Problem from '@/components/lp/problem'
import Logo from '@/components/lp/logo'
import Process from '@/components/lp/process'
import Pay from '@/components/lp/pay'
import ExampleTalbe from '@/components/lp/exampleTable'
import OptionList from '@/components/lp/optionList'
import CostTable from '@/components/lp/costTable'
import { Tab } from '@headlessui/react'
import { classNames } from '@/utils'
import Inquiry from '@/components/lp/inquiry'
import Link from 'next/link'
import Footer from '@/components/Footer'

type Props = {
  toThanks: () => void
}

const Container: FC<Props> = ({ toThanks }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  return (
    <main className="flex flex-col items-center justify-center">
      <section className="relative flex justify-center w-full">
        <div className="absolute top-0 right-0">
          <div className="quarter-circle -rotate-90" />
        </div>
        <img src="/wave.svg" alt='電波の波のアイコン' className="right-2 bottom-1/3 lg:right-1/2 xl:right-[45%] lg:bottom-1/2 absolute" />
        <img src="/wifi.svg" alt='wifiのアイコン' className="left-[8%] top-[30%] lg:right-1/2 xl:right-[45%] lg:top-[20%] absolute" />
        <img src="/router.svg" alt='routerのアイコン' className="right-[5%] top-[10%] lg:right-[35%] lg:top-1/3 absolute" />
        <div className="lp-container my-12 z-10">
          <div className='flex justify-center md:justify-start'>
            <img className="w-[90px] sm:w-[150px] sm:h-[44px]" src="/logo.png" alt="IotBiz" />
          </div>
          <div className="w-full flex flex-col mt-10 md:flex-row items-center gap-4">
            <div className="flex flex-col items-center md:items-end gap-3 md:gap-5">
              <h1 className="text-3xl text-center md:text-5xl text-primary-900 md:text-right font-bold">
                1枚からすぐに始められるIoT/M2M向け法人SIM
              </h1>
              <div>
                <p className="text-xl text-center md:text-2xl md:text-right text-primary-800">最短即日発送・縛りなし・利用シーン</p>
                <p className="text-xl text-center md:text-2xl md:text-right">に合わせたプランをご用意</p>
              </div>

              <img className="lg:hidden" src="/lp-main.svg" alt="first view image" />

              <div className="flex flex-col items-center z-10">
                <p className="relative text-left leading-none before:absolute before:bottom-[1px] before:left-[-10px] before:border-r-2 before:border-primary-900 before:inline-block before:h-5 before:-rotate-[30deg] after:absolute after:bottom-[1px] after:right-[-10px] after:border-l-2 after:border-primary-900 after:inline-block after:h-5 after:rotate-[30deg]">
                  SIM発行手数料 <span className="inline-block text-5xl text-primary-800 font-bold leading-none">0</span>
                  円キャンペーン{' '}
                </p>
                <Link href="#cv">
                  <a className="bg-accent-500 text-xl leading-none py-5 px-6 rounded-full shadow hover:shadow-md">
                    コンサルタントに詳しく相談
                  </a>
                </Link>
              </div>
              <p className="text-xs text-right z-10">
                <span>※別途契約事務手数料等がかかります。</span>
                <br />
                <span>適用条件は担当者にお問い合わせください。</span>
              </p>
            </div>
            <img className="w-[500px] h-[500px] hidden lg:block" src="/lp-main.svg" alt="first view image" />
          </div>
        </div>
        <div className="absolute bottom-5 left-0">
          <div className="lg-quarter-circle rotate-90" />
        </div>
        <div className="absolute bottom-0 bg-primary-800 w-full h-8" />
      </section>

      <section>
        <div className="my-8 md:my-28 bg-white relative lp-container">
          <h2 className="text-secondary-700 text-2xl font-semibold -top-4 left-4 md:-rotate-[15deg] absolute md:top-0 md:-left-14">
            こんなお悩み解決できます
          </h2>
          <div className="py-6 px-[14px] md:p-16 flex flex-col gap-8 md:gap-28">
            <Problem
              title="１.どんなSIMを選べばいいかわからない"
              url="/FAQ.svg"
              imageSize="500px"
              solutions={[
                {
                  subTitle: '通信量や自社にあったプランが知りたい',
                  details:
                    '100以上のプランの中から利用シーン、データ容量、回線数などに応じ最適なプランをご提案します。',
                },
                {
                  subTitle: '利用する機器に使えるか動作確認をしたい',
                  details:
                    '機器との接続や通信品質などのテストにSIMカードの貸し出しに対応しており、納得して利用開始いただけます。',
                },
              ]}
            />

            <Problem
              title="2.大手では条件が合わない"
              url="/barricade.svg"
              imageSize="425px"
              solutions={[
                {
                  subTitle: '発注の最小ロット数、契約期間を満たせない',
                  details: '契約期間なし、1枚単位からのお申し込みが可能です。',
                },
                {
                  subTitle: '納期が長い',
                  details: '物流体制を内製化しているためご契約完了後、最短即日発送が可能です。',
                  attention: '※特殊なSIMプランの場合、お時間を要する場合がございます。',
                },
              ]}
              isImageRight={true}
            />

            <Problem
              title="3.最適なSIMで余計なコストを抑えたい"
              url="/cost.svg"
              imageSize="500px"
              solutions={[
                {
                  subTitle: '利用プランの柔軟性がない',
                  details:
                    '上り優先やカメラ大容量向けなど利用シーンに合わせたプランの提案により、コスト最適化のサポートします。',
                },
                {
                  subTitle: '利用状況に合わせてプランの変更ができない',
                  details: '回線ごとにプラン変更が可能で、利用状況に合わせて最適なプランの選択がいただけます。',
                },
              ]}
            />
          </div>
        </div>
      </section>

      <section className="bg-primary-800 w-full flex justify-center">
        <div className="lp-container flex flex-col items-center py-10 gap-8 md:py-24 md:gap-20">
          <h2 className="text-white font-semibold text-2xl">「IoTBiz」が選ばれる理由</h2>
          <div className="flex gap-8 md:gap-14 justify-center flex-wrap">
            <div className="flex flex-col gap-2 items-center">
              <img src="/one-piece.svg" className="w-[300px] h-[200px] bg-white" alt="一枚から始められる" />
              <p className="text-white font-semibold">一枚から始められる</p>
            </div>
            <div className="flex flex-col gap-2 items-center">
              <img src="/shaking-hands.svg" className="w-[300px] h-[200px] bg-white" alt="契約期間縛りなし" />
              <p className="text-white font-semibold">契約期間縛りなし</p>
            </div>
            <div className="flex flex-col gap-2 items-center">
              <img src="/shipping.svg" className="w-[300px] h-[200px] bg-white" alt="即日最短発送" />
              <p className="text-white font-semibold">即日最短発送</p>
            </div>
          </div>

          <Link href="#cv">
            <a className="bg-accent-500 text-3xl leading-none py-5 px-10 sm:px-16 rounded-full shadow hover:shadow-md">詳しく相談する</a>
          </Link>

        </div>
      </section>

      <section>
        <div className="lp-container bg-white relative px-[14px] md:px-[36px] my-10 md:my-28">
          <h2 className="text-secondary-700 text-2xl font-semibold absolute -top-4">導入企業・業界</h2>
          <div className="flex flex-col lg:flex-row items-center md:justify-between pt-8 pb-4 lg:py-16 gap-8">
            <Logo src="/seqsense.png" alt="seqsenceのlogo" desc="ロボット" />
            <Logo src="/nittetu.png" alt="日鉄エンジニアリングのlogo" desc="プラントエンジニアリング" />
            <Logo src="/echohai.png" alt="echohaiのlogo" desc="ロジスティックス" />
          </div>

          <div className="flex flex-col lg:flex-row items-center md:justify-between pt-4 pb-8 lg:py-16 gap-8">
            <Logo src="/lighthouse.png" alt="lighthouseのlogo" desc="漁業テック" />
            <Logo src="/mowcam.png" alt="mowcamのlogo" desc="畜産テック" />
            <Logo src="しろくま電力 (3).jpg" alt="afterFITのlogo" desc="エネルギー" />
          </div>
        </div>
      </section>

      <section className="bg-primary-800 w-full flex justify-center">
        <div className="lp-container flex flex-col items-center py-16 gap-10">
          <h2 className="text-white font-semibold text-2xl">活用例別おすすめプラン</h2>
          <div className="bg-white pt-1 pb-4 md:p-6 lg:p-10">
            <div className="flex flex-col items-center xs:w-[342px] md:w-[700px]">
              <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
                <Tab.List className="w-full flex">
                  <Tab
                    className={({ selected }) =>
                      classNames(
                        'py-2 px-4 text-xl leading-1 text-primary-900 flex-1',
                        selected
                          ? 'text-primary-800 rounded-[4px] rounded-b-none border-t border-r border-l border-divided border-b-none'
                          : 'w-full border-b border-divided'
                      )
                    }
                  >
                    上り下り高速
                  </Tab>
                  <Tab
                    className={({ selected }) =>
                      classNames(
                        'py-2 px-4 text-xl leading-1 text-primary-900 flex-1',
                        selected
                          ? 'text-primary-800 rounded-[4px] rounded-b-none border-t border-r border-l border-divided border-b-none'
                          : 'border-b border-divided'
                      )
                    }
                  >
                    上り優先
                  </Tab>
                  <Tab
                    className={({ selected }) =>
                      classNames(
                        'py-2 px-4 text-xl leading-1 text-primary-900 flex-1',
                        selected
                          ? 'text-primary-800 rounded-[4px] rounded-b-none border-t border-r border-l border-divided border-b-none'
                          : 'border-b border-divided'
                      )
                    }
                  >
                    カメラ大容量
                  </Tab>
                </Tab.List>
                <Tab.Panels className="px-[14px]">
                  <Tab.Panel>
                    <div className="my-8">
                      <ExampleTalbe
                        examples={[
                          { title: 'デジタルサイネージ', color: 'primary-800' },
                          { title: 'スマートフォン・タブレット', color: 'accent-500' },
                        ]}
                        columns={{
                          prices: [
                            '500円/月',
                            '900円/月',
                            '1390円/月',
                            '2200円/月',
                            '3300円/月',
                            '7500円/月',
                            'お問合せください',
                          ],
                          bands: ['1GB', '3GB', '6GB', '12GB', '20GB', '50GB', '50GB以上'],
                          colors: [
                            ['accent-600', 'transparent'],
                            ['accent-500', 'transparent'],
                            ['accent-500', 'transparent'],
                            ['accent-500', 'primary-800'],
                            ['transparent', 'primary-800'],
                            ['transparent', 'primary-800'],
                            ['transparent', 'transparent'],
                          ],
                        }}
                      />
                    </div>
                    <div className="my-8">
                      <OptionList
                        title="その他のオプション"
                        options={['データリチャージ', '固定IPアドレス', '5Gオプション']}
                      />
                    </div>

                    <div className="my-8">
                      <CostTable
                        title="費用例"
                        case={{ type: 'デジタルサイネージ', band: '20GB', price: '3300円/回線' }}
                      />
                    </div>
                  </Tab.Panel>
                  <Tab.Panel>
                    <div className="my-8">
                      <ExampleTalbe
                        examples={[
                          { title: '防犯・監視カメラ', color: 'primary-900' },
                          { title: 'センサー', color: 'accent-500', desc: 'ex ) 太陽光発電・農業・漁業の各種センサー' },
                        ]}
                        columns={{
                          prices: [
                            '450円/月',
                            '850円/月',
                            '1250円/月',
                            '2100円/月',
                            '2950円/月',
                            '7000円/月',
                            'お問合せください',
                          ],
                          bands: ['1GB', '3GB', '6GB', '12GB', '20GB', '50GB', '50GB以上'],
                          colors: [
                            ['accent-500', 'transparent'],
                            ['accent-500', 'transparent'],
                            ['accent-500', 'transparent'],
                            ['accent-500', 'primary-800'],
                            ['transparent', 'primary-800'],
                            ['transparent', 'primary-800'],
                            ['transparent', 'transparent'],
                          ],
                        }}
                      />
                    </div>
                    <div className="my-8">
                      <OptionList
                        title="その他のオプション"
                        options={['データリチャージ', '固定IPアドレス', '5Gオプション']}
                      />
                    </div>

                    <div className="my-8">
                      <CostTable title="費用例" case={{ type: 'センサー', band: '6GB', price: '1250円/回線' }} />
                    </div>
                  </Tab.Panel>
                  <Tab.Panel>
                    <div className="my-8">
                      <ExampleTalbe
                        examples={[{ title: 'カメラ', color: 'primary-800' }]}
                        columns={{
                          prices: ['4000円/月', '5200円/月', '8900円/月', 'お問合せください'],
                          bands: ['50GB', '100GB', '200GB', '400GB以上'],
                          colors: [['primary-800'], ['primary-800'], ['primary-800'], ['primary-800']],
                        }}
                      />
                    </div>
                    <div className="my-8">
                      <OptionList title="その他のオプション" options={['固定IPアドレス', '利用休止オプション']} />
                    </div>

                    <div className="my-8">
                      <CostTable title="費用例" case={{ type: '監視カメラ', band: '100GB', price: '5200円/回線' }} />
                    </div>
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>

              {selectedIndex === 0 && (
                    <>
                      <h3 className="text-xl font-semibold text-secondary-700 my-4">キャンペーン</h3>
                      <div className="relative text-center mb-2 leading-none before:absolute before:bottom-[1px] before:-left-[16px] before:border-r-2 before:border-primary-900 before:inline-block before:h-8 sm:before:h-12 before:-rotate-[30deg] after:absolute after:bottom-[1px] after:-right-[16px] after:border-l-2 after:border-primary-900 after:inline-block after:h-8 sm:after:h-12 after:rotate-[30deg] text-[14px] md:text-base">
                        <p>SIM発行費用は通常394円/回線のところ</p>
                        <p>
                          事例紹介のご協力いただけるお客様は
                          <span className="inline-block text-4xl text-primary-800 font-bold leading-none">0</span>円
                        </p>
                      </div>
                      <Link href="#cv">
                        <a className="bg-accent-500 text-3xl leading-none py-5 px-10 sm:px-16 rounded-full shadow hover:shadow-md">詳しく相談する</a>
                      </Link>
                    </>
                  )}

                  {selectedIndex === 1 && (
                    <>
                      <Link href="https://mvno.dxhub.co.jp/iotbiz-large-upload" passHref>
                        <a className="bg-accent-500 text-3xl leading-none py-5 px-10 sm:px-16 rounded-full shadow hover:shadow-md" target="_blank" rel="noopener noreferrer">
                          プランの詳細を見る
                        </a>
                      </Link>
                    </>
                  )}

                  {selectedIndex === 2 && (
                    <>
                      <Link href="https://mvno.dxhub.co.jp/iotbiz-large-upload" passHref>
                        <a className="bg-accent-500 text-3xl leading-none py-5 px-10 sm:px-16 rounded-full shadow hover:shadow-md" target="_blank" rel="noopener noreferrer">
                          プランの詳細を見る
                        </a>
                      </Link>
                    </>
                  )}
              <p className="text-xs mt-9 place-self-end">*表示金額はすべて税抜表記となります。</p>
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="lp-container bg-white relative px-[14px] mt-10 md:px-9 md:mt-28 mb-10">
          <h2 className="text-secondary-700 text-2xl font-semibold absolute -top-4">利用開始までの流れ</h2>
          <div className="flex flex-col md:flex-row items-center gap-10 lg:justify-between pt-8 pb-4 md:py-16">
            <Process
              src="/p-1.svg"
              title="お見積もり・お申し込みのご依頼"
              desc="お問い合わせフォームよりお客様がどういった通信サービスをご要望されているか、ご相談ください。"
            />
            <Process
              src="/p-2.svg"
              title="最適なプランのご提案"
              desc="弊社のIoT/M2M専門コンサルタントからお客様のご要望に合わせて最適な通信プランを提案させていただきます。"
            />
          </div>
          <div className="flex flex-col md:flex-row items-center gap-10 lg:justify-between pt-4 pb-8 md:py-16">
            <Process
              src="/p-3.svg"
              title="ご契約後、最短即日の発送"
              desc="すぐにSIMカードを発送させていただきます。物流体制を内製化しているため最短即日での発送が可能です。"
            />
            <Process
              src="/p-4.svg"
              title="受取・SIMカードの挿入・開通"
              desc="受け取ったSIMカードを端末に取り付け、接続設定を行えばすぐ利用開始できます。"
            />
          </div>
        </div>
      </section>

      <section>
        <div className="lp-container bg-white relative px-[36px] mb-10 md:mb-28">
          <h2 className="text-secondary-700 text-2xl font-semibold absolute -top-4">支払い方法</h2>
          <p className="pt-14 text-gray-600">
            お支払い方法は銀行口座振込、口座振替、コンビニ払いの3種類からお選びいただけます。
          </p>
          <div className="flex flex-wrap justify-center md:justify-between py-10 gap-10 md:gap-0">
            <Pay src="/ATM.svg" title="銀行振込" />
            <Pay src="/Online.svg" title="口座振替" />
            <Pay src="/Cash.svg" title="コンビニ支払い" />
          </div>
          <p className="pb-4 text-gray-600 text-sm">
            ※お支払いはPaidサービスを利用し、株式会社ラクーンフィナンシャルよりご請求させていただきます。
          </p>
        </div>
      </section>

      <section className="bg-primary-800 w-full flex justify-center">
        <div id="cv" className="lp-container flex flex-col items-center py-16 gap-10">
          <h2 className="text-white font-semibold text-2xl">問い合せ</h2>
          <div className="bg-white flex flex-col items-center py-9 xs:w-[342px] md:w-[700px] lg:w-[900px]">
            <div className="w-full px-[14px] md:w-[600px]">
              <Inquiry toThanks={toThanks} />
            </div>
          </div>
        </div>
      </section>
      <Footer />  
    </main>
  )
}

export default Container
